//#region IMPORTS
import {
  RowData,
  ColumnDef,
  flexRender,
  CellContext,
  useReactTable,
  ExpandedState,
  getCoreRowModel,
  createColumnHelper,
  getExpandedRowModel,
  ColumnFiltersState,
  getFilteredRowModel,
} from "@tanstack/react-table";
import {
  EyeIcon,
  EyeSlashIcon,
  CodeBracketSquareIcon,
} from "@heroicons/react/24/outline";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { InstoreOrderLineTableRow } from "../../models/instoreOrder";
import { ADD_PERSO_VALUES_MODAL } from "../../models/_consts";
import { SquaresPlusIcon } from "@heroicons/react/20/solid";
import { useAppDispatch } from "../../store/configureStore";
import { useTranslation } from "react-i18next";
import { StoreFormat } from "../../models/storeFormat";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { Tooltip } from "react-tooltip";
import InstoreOrderDetailTableEditableCell from "./InstoreOrderDetailTableEditableCell";
import InstoreOrderDetailTableBody from "./InstoreOrderDetailTableBody";
import MainTableRowActionDelete from "../_layout/MainTableRowActionDelete";
import MainTableRowActionCreate from "../_layout/MainTableRowActionCreate";
import classNames from "../../helpers/classNames";

declare module "@tanstack/react-table" {
  interface TableMeta<TData extends RowData> {
    updateData: (columnId: string, value: unknown, row: TData) => void;
  }
}
//#endregion

//#region INTERFACE
interface Props {
  setSelectedCellContext: (
    item: CellContext<InstoreOrderLineTableRow, any[]>,
  ) => void;
  updateRowCellsHandler?: (
    columnId: string,
    value: any,
    row: InstoreOrderLineTableRow,
  ) => void;
  addParentRowHandler?: () => void;
  addChildRowHandler?: (themeId: number) => void;
  deleteRowHandler?: (row: InstoreOrderLineTableRow) => void;
  storeFormatToUse: StoreFormat;
  disabled?: boolean;
  readOnly?: boolean;
  moveRow?: (toIndex: number, row: InstoreOrderLineTableRow) => void;
  data: InstoreOrderLineTableRow[];
}
//#endregion

export default function InstoreOrderDetailTable({
  setSelectedCellContext,
  updateRowCellsHandler,
  addParentRowHandler,
  addChildRowHandler,
  deleteRowHandler,
  storeFormatToUse,
  disabled = false,
  readOnly = false,
  moveRow,
  data,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // Local state
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  //#endregion

  //#region LOGIC
  useEffect(() => {
    setExpanded(true);
  }, []);

  const addOrShowPersoValuesModalHandler = (
    item: CellContext<InstoreOrderLineTableRow, any[]>,
  ) => {
    setSelectedCellContext(item);
    dispatch(openModal(ADD_PERSO_VALUES_MODAL));
  };

  const hasPersoFilter = (row: any, columnId: string, filterValue: boolean) => {
    if (!filterValue) return true;
    const original = row.original;

    if (original.parent) return false;

    return (
      Array.isArray(original.custom_fields) && original.custom_fields.length > 0
    );
  };

  //#endregion

  //#region REACT TABLE COLUMNS
  const columns = useMemo<ColumnDef<InstoreOrderLineTableRow, any>[]>(() => {
    const columnHelper = createColumnHelper<InstoreOrderLineTableRow>();
    const baseColumns: ColumnDef<InstoreOrderLineTableRow, any>[] = [
      columnHelper.accessor(({ theme_id }) => theme_id, {
        id: "theme_id",
        size: 250,
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            {row.original.parent ? (
              <button
                {...{
                  type: "button",
                  disabled,
                  onClick: row.getToggleExpandedHandler(),
                  style: { cursor: row.getCanExpand() ? "pointer" : "normal" },
                }}
              >
                {row.getIsExpanded() ? (
                  <EyeSlashIcon className="h-4 w-4 ml-2 text-purple-600" />
                ) : (
                  <EyeIcon className="h-4 w-4 ml-2 text-emerald-600" />
                )}
              </button>
            ) : (
              <div className="w-6 h-4" />
            )}

            <div className="w-8 flex justify-center">
              {row.original.parent && !readOnly && addChildRowHandler ? (
                <MainTableRowActionCreate
                  tooltipContent="Add new article to theme"
                  disabled={disabled || !row.getIsExpanded()}
                  tooltipId={String(row.original.theme_id)}
                  createHandler={() => {
                    addChildRowHandler(row.original.theme_id);
                  }}
                />
              ) : (
                <div className="w-4 h-4" />
              )}
            </div>

            <span
              className={classNames(row.getCanExpand() ? "font-semibold" : "")}
            >
              {row.original.parent ? "THEME" : "ARTICLE"}
            </span>
          </div>
        ),
        header: ({ table }) => (
          <div className="flex items-center gap-3">
            <button
              {...{
                type: "button",
                disabled,
                onClick: table.getToggleAllRowsExpandedHandler(),
              }}
            >
              {table.getIsAllRowsExpanded() ? (
                <EyeSlashIcon className="h-4 w-4 ml-2 text-purple-600" />
              ) : (
                <EyeIcon className="h-4 w-4 ml-2 text-emerald-600" />
              )}
            </button>
            {!readOnly ? (
              <Fragment>
                <button
                  data-tooltip-delay-show={100}
                  data-tooltip-content="Add new theme group"
                  data-tooltip-place="right"
                  data-tooltip-id="ea197269-0a25-4149-ba0c-21760aa2b3b4"
                  className={classNames(
                    disabled
                      ? "hover:cursor-not-allowed bg-gray-400"
                      : "hover:cursor-pointer hover:bg-pink-500 bg-pink-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600",
                    "rounded-full p-2 text-white h-8 w-8 flex items-center justify-center",
                  )}
                  disabled={disabled}
                  onClick={addParentRowHandler}
                  type="button"
                >
                  <SquaresPlusIcon aria-hidden="true" className="h-4 w-4" />
                </button>
                <Tooltip
                  id="ea197269-0a25-4149-ba0c-21760aa2b3b4"
                  className="font-bold"
                />
              </Fragment>
            ) : (
              <div className="w-8 h-8" />
            )}
            row kind
          </div>
        ),
      }),
      columnHelper.accessor(({ article_name }) => article_name, {
        id: "article_name",
        // wide enough that long theme/article names don't run under set/price
        size: 600,
        cell: (item) =>
          item.row.original.parent ? (
            <span className="ml-3">{item.getValue()}</span>
          ) : (
            <InstoreOrderDetailTableEditableCell
              {...item}
              disabled={disabled || readOnly || !item.row.original.placeholder}
            />
          ),
        enableGrouping: false,
        header: () => <span className="ml-3">article</span>,
      }),
      // set depth: editable for Admin only (colored cell); read-only white
      // otherwise. price: always read-only white — not editable on this screen.
      // Both set depth and price are read-only here — they can only be set on
      // the template. White background, never editable.
      columnHelper.accessor(({ set }) => set, {
        id: "set",
        size: 320,
        cell: (item) =>
          item.row.original.parent ? null : (
            <InstoreOrderDetailTableEditableCell {...item} disabled />
          ),
        enableGrouping: false,
        header: () => t("rituals.setDepth"),
      }),
      columnHelper.accessor(({ price }) => price, {
        id: "price",
        size: 260,
        // Always disabled → white background, since price can't be edited here.
        cell: (item) =>
          item.row.original.parent ? null : (
            <InstoreOrderDetailTableEditableCell {...item} disabled />
          ),
        enableGrouping: false,
        header: () => `${t("rituals.price")} (${t("rituals.inEuro")})`,
      }),
      columnHelper.accessor(({ custom_fields }) => custom_fields, {
        id: "custom_fields",
        size: 200,

        cell: (item) =>
          item.row.original.parent ||
          !item.getValue() ||
          item.getValue().length <= 0 ? null : (
            <Fragment>
              <button
                disabled={disabled}
                className="hover:bg-purple-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 bg-white flex items-center gap-x-1.5 rounded-md  px-1 py-1 text-sm font-semibold"
                onClick={() => addOrShowPersoValuesModalHandler(item)}
                type="button"
              >
                <CodeBracketSquareIcon className="text-purple-600 hover:text-purple-800 w-4 h-4" />
              </button>
            </Fragment>
          ),
        enableGrouping: false,
        enableColumnFilter: true,
        filterFn: hasPersoFilter,

        header: ({ column }) => {
          const isActive = column.getFilterValue() === true;

          return (
            <button
              type="button"
              onClick={() => column.setFilterValue(isActive ? undefined : true)}
              className={`flex items-center gap-1 rounded px-2 py-1 text-sm font-semibold
            ${isActive ? "bg-purple-200 text-purple-800" : "bg-white"}
          `}
            >
              perso
              <CodeBracketSquareIcon className="w-4 h-4" />
            </button>
          );
        },
      }),
      columnHelper.accessor((row) => row.amount, {
        enableGrouping: false,
        enableSorting: false,
        size: 300,
        id: "amount",
        cell: (item) =>
          item.row.original.parent ? null : (
            <InstoreOrderDetailTableEditableCell
              {...item}
              disabled={
                disabled ||
                readOnly ||
                item.getValue() !== 0 ||
                item.row.original.placeholder
              }
            />
          ),
        header: () => {
          return (
            <span className="font-extrabold text-sky-800">
              {storeFormatToUse.name}
            </span>
          );
        },
      }),
    ];

    const actionColumns = [
      columnHelper.accessor((row) => row, {
        enableGrouping: false,
        enableSorting: false,
        size: 200,
        id: "actions",
        cell: ({ row }) => {
          return (
            <div className="flex justify-center items-center">
              {!readOnly && deleteRowHandler ? (
                <MainTableRowActionDelete
                  tooltipContent="Remove item from list"
                  disabled={disabled}
                  deleteHandler={() => deleteRowHandler(row.original)}
                  tooltipId={String(row.id)}
                />
              ) : null}
            </div>
          );
        },
        header: () => null,
      }),
    ];

    return [...baseColumns, ...actionColumns];
  }, [
    addParentRowHandler,
    addChildRowHandler,
    storeFormatToUse,
    deleteRowHandler,
    disabled,
    readOnly,
    t,
  ]);
  //#endregion

  //#region REACT TABLE INSTANCE
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const table = useReactTable({
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    filterFromLeafRows: true,
    getSubRows: (originalRow) => originalRow.children,
    columns,
    state: { expanded, columnFilters },
    data: data,
    meta: {
      updateData: (
        columnId: string,
        value: any,
        row: InstoreOrderLineTableRow,
      ) => {
        !readOnly &&
          updateRowCellsHandler &&
          updateRowCellsHandler(columnId, value, row);
      },
    },
  });
  //#endregion

  //#region RENDER
  return (
    <div
      ref={tableContainerRef}
      style={{
        overflow: "auto", //our scrollable table container
        position: "relative", //needed for sticky header
        height: "910px", //should be a fixed height
      }}
    >
      <table
        className={classNames(
          disabled ? "opacity-50 pointer-events-none" : "opacity-100",
          "min-w-full divide-y divide-gray-300 grid",
        )}
      >
        <thead className="sticky top-0 bg-white/80 h-16 z-10 grid">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="flex items-center w-full">
              {headerGroup.headers.map((header) => {
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className="whitespace-nowrap pr-2 py-3.5 text-left text-xs text-gray-900"
                    // Columns scale proportionally to their `size` and always
                    // fill the container, so the table fits both the wide edit
                    // screen and the narrower summary — header stays aligned
                    // with the body (which uses the same rule).
                    style={{
                      flexGrow: header.getSize(),
                      flexShrink: 1,
                      flexBasis: 0,
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <div>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <InstoreOrderDetailTableBody
          tableContainerRef={tableContainerRef}
          moveRow={moveRow!}
          table={table}
        />
      </table>
    </div>
  );
  //#endregion
}
